import "@geoman-io/leaflet-geoman-free";
import "@raruto/leaflet-elevation";
import L from "leaflet";
import { length } from "@turf/turf";

import { addAltitudeToLayer, getElevationGainAndLoss } from "./elevation.js";

const pathOptions = { className: "route" };

const pmOptions = {
  tooltips: false,
  allowSelfIntersection: true,
  markerStyle: { draggable: false },
  // eslint-disable-next-line unicorn/no-null
  finishOn: null,
  templineStyle: { className: "route is-drawing" },
  hintlineStyle: { className: "route is-drawing" },
  pathOptions,
};

const elevationOptions = {
  theme: "kartorama-theme",
  detached: false,
  autohide: true, // If (!detached) autohide chart profile on chart mouseleave
  collapsed: false, // If (!detached) initial state of chart profile control
  position: "topright", // If (!detached) control position on one of map corners
  followMarker: false, // Autoupdate map center on chart mouseover.
  imperial: false, // Chart distance/elevation units.
  reverseCoords: false, // [Lat, Long] vs [Long, Lat] points. (leaflet default: [Lat, Long])
  summary: true,
  legend: false,
  ruler: false,
  width: 400,
  height: 150,
  responsive: true,
};

const drawingOptions = {
  position: "bottomleft",
  drawCircle: false,
  drawMarker: false,
  drawCircleMarker: false,
  drawRectangle: false,
  drawPolygon: false,
  dragMode: false,
  drawText:false,
  cutPolygon: false,
  rotateMode: false,
  removalMode: false,
  editMode: false,
  drawPolyline: true,
};

function generateStats(geojson) {
  const distance = length(geojson).toFixed(1);
  const { elevationGain, elevationLoss } = getElevationGainAndLoss(geojson);
  return { distance, elevationGain, elevationLoss };
}

function getElevationLine(routeLayer) {
  return routeLayer.controlElevationProfile._layers._layers;
}

function deactivateElevationLine(routeLayer) {
  const elevationLine = routeLayer.controlElevationProfile._layers._layers;

  if (Object.keys(elevationLine).length > 0) {
    Object.values(elevationLine)[0].remove();
  }
}

function hideElevationProfile(layer) {
  layer.controlElevationProfile._container.style.display = "none";
}

function getLastUserRoute(map) {
  const layers = map.routeLayers.getLayers();
  return layers.pop().toGeoJSON();
}

function RouteHandler(map) {
  function showElevationProfile(layer) {
    layer.controlElevationProfile._container.style.display = "block";
  }

  function activateElevationLine(routeLayer) {
    const elevationLine = getElevationLine(routeLayer);
    if (Object.keys(elevationLine).length > 0) {
      Object.values(elevationLine)[0].addTo(map);
    }
  }

  function deactivateRoute(routeLayer) {
    hideElevationProfile(routeLayer);
    deactivateElevationLine(routeLayer);
    map.state.activeRouteLayer = undefined;
  }

  function setActiveRoute(routeLayer) {
    if (map.state.activeRouteLayer) {
      deactivateRoute(map.state.activeRouteLayer);
    }

    activateElevationLine(routeLayer);
    showElevationProfile(routeLayer);
    // routeLayer.openPopup();
    map.state.activeRouteLayer = routeLayer;
  }

  // eslint-disable-next-line max-statements
  function initElevationLayer(layer) {
    const geojsonWithElevation = layer.toGeoJSON();
    layer.controlElevationProfile = L.control.elevation(elevationOptions);
    layer.controlElevationProfile.addTo(map);
    const data = generateStats(geojsonWithElevation);
    const summary = `Distanse: ${data.distance} km. Opp: ${data.elevationGain} m. Ned: ${data.elevationLoss} m. `;

    layer.controlElevationProfile.on("elechart_summary", () => {
      // The plugin populates its default summary after firing this event.
      queueMicrotask(() => {
        const summaryElement = layer.controlElevationProfile
          .getContainer()
          ?.querySelector(".elevation-summary");
        if (summaryElement) {
          summaryElement.textContent = summary;
        }
      });
    });
    // The plugin's load method parses a string, not a GeoJSON object.
    layer.controlElevationProfile.load(JSON.stringify(geojsonWithElevation));

    map.fitBounds(layer.getBounds());

    layer.setStyle(pathOptions);
    map.addLayer(layer);
    map.routeLayers.addLayer(layer);
    setActiveRoute(layer);

    layer.on("click", (event) => {
      setActiveRoute(event.target);
      L.DomEvent.stopPropagation(event);
    });
  }

  function distanceInKm(latlng1, latlng2) {
    const METERS_IN_KM = 1000;

    return map.distance(latlng1, latlng2) / METERS_IN_KM;
  }

  function initDrawingTooltip() {
    map.on("pm:drawstart", ({ workingLayer }) => {
      let totalDistance = 0;
      // eslint-disable-next-line init-declarations
      let lastLatlng;
      const tooltip = L.tooltip();
      workingLayer.bindTooltip(tooltip);

      workingLayer.on("pm:vertexadded", (event) => {
        const currentLatlng = event.latlng;
        if (lastLatlng) {
          totalDistance += distanceInKm(lastLatlng, currentLatlng);
          workingLayer.setTooltipContent(`${totalDistance.toFixed(1)} km`);
          workingLayer.openTooltip(event.latlng);
        }

        lastLatlng = currentLatlng;
      });

      map.on("mousemove", (event) => {
        if (!lastLatlng) {
          return;
        }

        const currentLatlng = event.latlng;
        const currentDistance =
          distanceInKm(lastLatlng, currentLatlng) + totalDistance;
        workingLayer.setTooltipContent(`${currentDistance.toFixed(1)} km`);
        workingLayer.openTooltip(event.latlng);
      });
    });
  }

  async function addElevationAndReplace(layer) {
    let layerWithElevation;
    try {
      layerWithElevation = await addAltitudeToLayer(layer);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Kunne ikke hente høydedata", error);
      // eslint-disable-next-line no-alert
      alert("Kunne ikke hente høydedata for ruten. Prøv igjen senere.");
      return;
    }
    map.removeLayer(layer);
    initElevationLayer(layerWithElevation);
  }

  return {
    init() {
      map.on("click", () => {
        if (map.state.activeRouteLayer) {
          deactivateRoute(map.state.activeRouteLayer);
        }
      });

      map.routeLayers = L.layerGroup();

      map.pm.setGlobalOptions(pmOptions);
      map.pm.setLang("no");
      map.pm.addControls(drawingOptions);

      map.on("pm:create", (event) => {
        // eslint-disable-next-line no-console
        console.log(JSON.stringify(event.layer.toGeoJSON()));
        addElevationAndReplace(event.layer);
      });

      initDrawingTooltip();
    },

    addElevationAndReplace,
  };
}

export { RouteHandler, getLastUserRoute };
